import React, { useEffect, useState, Fragment } from 'react'
import './style.sass'
import PropTypes from 'prop-types'
import FieldWrapper from '../FieldWrapper'
import { Collapse, ListGroup } from 'react-bootstrap'
import Arrow from '../../../../assets/icons/arrow-right.svg'
import MultiSelectListItem from './MultiSelectListItem'
import PillContainer from '../../PillContainer'

const MultiSelectField = ({ displayName, placeholder, description, errorMessage, disabled, options, value, onChange = () => {} }) => {

  const [open, setOpen] = useState(false)
  const [seletedList, setSeletedList] = useState([])

  useEffect(() => {
    // init seletedList
    if (value && Array.isArray(value)) {
      setSeletedList(value)
    }
  }, [value])

  // This method handles the selection of normal options
  const onItemSelected = (isSeleted, option) => {
    let updatedSeletedList = []

    if (isSeleted) {
      // Add to selected list
      updatedSeletedList = [...seletedList, option]
    } else {
      // Remove from selected list
      updatedSeletedList = seletedList.filter(item => item.value !== option.value)
    }

    setSeletedList(updatedSeletedList)
    onChange(updatedSeletedList)
  }
  
  // This method handles the selection of normal header options
  const onHeaderItemSelected = (isSeleted, option) => {
    let updatedSeletedList = []

    // Getting a list after removing this option's inner options from the seletedList
    const listWithoutInnerOptions = seletedList.filter(item => !option.list.some(innerOption => item.value === innerOption.value))

    if (isSeleted) {
      // Adding all inner options
      updatedSeletedList = [...listWithoutInnerOptions, ...option.list]
    } else {
      // Assigning the list without inner options as the updated seleted list
      updatedSeletedList = listWithoutInnerOptions
    }

    setSeletedList(updatedSeletedList)
    onChange(updatedSeletedList)
  }
  
  // This method computes if the option is selected or not 
  const computeIsSeleted = (option, isHeaderItem) => {
    if (isHeaderItem) {
      // Checking if all inner options are in the seletedList
      return option.list?.every(x => seletedList?.some(y => y.value === x.value))
    }
    // Check if option is in seletedList
    return seletedList?.some(x => x.value === option.value)
  }

  // This method renders the options
  const renderOptions = () => {
    return options?.map((option, i) => {
      // Flag to check if option list has inner options. If option hasInnerOptions, that option is considered to be a header item
      const hasInnerOptions = option.list && option.list.length > 0
      const isHeaderItem = hasInnerOptions
      return (
        <Fragment key={`${option.value}-${i}`} >
          <MultiSelectListItem 
            item={option}
            isSeleted={computeIsSeleted(option, isHeaderItem)} 
            onChange={(isSeleted) => !isHeaderItem ? onItemSelected(isSeleted, option) : onHeaderItemSelected(isSeleted, option)}
            isHeaderItem={isHeaderItem}
          />
          {!hasInnerOptions || option.list?.map(innerOption => (
            <MultiSelectListItem 
              key={`${innerOption.value}-${i}`} 
              item={innerOption}
              isSeleted={computeIsSeleted(innerOption)} 
              onChange={(isSeleted) => onItemSelected(isSeleted, innerOption)}
              isSubItem={true}
            />
          ))}
        </Fragment>
      )
    })
  }

  const getSelectValueDisplayName = (value) => {
    return options?.find(o => o.value === value)?.displayName ?? value
  }
  
  return (
    <FieldWrapper
      displayName={displayName}
      description={description} 
      errorMessage={errorMessage}
      disabled={disabled || options?.length === 0}
    >
      <div className="multi-select-field-container">
        <div>
          <div className={'multi-select-field-input'} onClick={!disabled ? () => setOpen(!open) : () => {}}>
            <label>
              {seletedList?.length ? `${seletedList?.length} selected` : `${placeholder || displayName}`}
            </label>
            <object 
              className={open ? 'angle-up-icon' : 'angle-down-icon'} 
              data={Arrow} 
              type="image/svg+xml" 
              aria-label="arrow"
            />
          </div>
          <Collapse in={disabled || options?.length === 0 ? false : open} className='collapse-content-container'>
            <ListGroup>
              {renderOptions()} 
            </ListGroup>
          </Collapse>
        </div>
        {seletedList?.length > 0 && options?.length > 0 ? (
          <PillContainer items={seletedList?.map(i =>  i.displayName || getSelectValueDisplayName(i.value))} />
        ) : null}
      </div>
    </FieldWrapper>
  )
}

MultiSelectField.propTypes = {
  displayName: PropTypes.string,
  placeholder: PropTypes.string,
  description: PropTypes.string,
  errorMessage: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
}

export default MultiSelectField
